import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Image as ImageIcon,
  Video,
  Plus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSignedDocumentUrl } from '../../hooks/useSignedDocumentUrl';
import { useStories } from '../../context/StoriesContext';
import { MediaEditorModal } from './MediaEditorModal';

/** Resolves imageUrl (key, blob, data URL) and renders video or img for story viewer. */
function StoryMediaView({
  imageUrl,
  isVideo,
}: {
  imageUrl?: string | null;
  isVideo?: boolean;
}) {
  const isDirectUrl =
    imageUrl &&
    (imageUrl.startsWith('blob:') ||
      imageUrl.startsWith('data:') ||
      imageUrl.startsWith('http'));
  const { url, loading } = useSignedDocumentUrl(
    isDirectUrl ? undefined : (imageUrl ?? undefined)
  );
  const displayUrl =
    (isDirectUrl ? imageUrl : url) ??
    'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800';
  if (!isDirectUrl && loading) {
    return (
      <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-black pointer-events-none">
        <span className="text-white/70">...</span>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-black pointer-events-none">
      {isVideo && imageUrl ? (
        <video
          src={displayUrl}
          className="w-full h-full object-contain"
          autoPlay
          playsInline
          muted
          loop
          controls
        />
      ) : (
        <img
          src={displayUrl}
          alt="Story"
          className="w-full h-full object-contain"
        />
      )}
    </div>
  );
}

/** Resolves src (key or URL) for story ring/header thumbnail; falls back to emoji/text. */
function StoryThumbnail({
  src,
  isVideo,
  className = 'w-full h-full object-cover',
}: {
  src: string;
  isVideo?: boolean;
  className?: string;
}) {
  const isBlobOrHttp =
    src.startsWith('blob:') ||
    src.startsWith('data:') ||
    src.startsWith('http://') ||
    src.startsWith('https://');
  const looksLikeKey = !isBlobOrHttp && (src.includes('/') || src.length > 20);
  const needsResolve = looksLikeKey || isBlobOrHttp;
  const { url, loading } = useSignedDocumentUrl(needsResolve ? src : undefined);

  if (!needsResolve) {
    return <span className="text-2xl">{src}</span>;
  }

  const displayUrl = isBlobOrHttp ? src : url;
  if (loading || !displayUrl) {
    return (
      <div
        className={
          className +
          ' flex items-center justify-center bg-[#F0EFF5] dark:bg-white/10'
        }
      >
        <span className="text-[#464452] dark:text-white/50 text-xs">…</span>
      </div>
    );
  }
  return isVideo ? (
    <video
      src={displayUrl}
      className={className}
      muted
      playsInline
      loop
      preload="metadata"
    />
  ) : (
    <img src={displayUrl} alt="" className={className} />
  );
}

interface StoriesProps {
  userType: 'owner' | 'professional';
  userId: string;
  pets: Array<{ id: string; name: string; avatar?: string; species?: string }>;
  /** When set (e.g. from Create Post "Also share to Your Story"), add this as the user's story. */
  addToStoryMedia?: {
    url: string;
    isVideo: boolean;
  };
  onStoryAdded?: () => void;
}

interface StoryRing {
  id: string;
  petName: string;
  petAvatar: string;
  hasUnviewed: boolean;
  isOwn?: boolean;
  /** Story items for this user (for multi-story scroll). */
  items?: Array<{
    id: string;
    url: string;
    isVideo: boolean;
    createdAt: number;
  }>;
}

const STORY_DURATION_MS = 5000;

export function Stories({
  userType: _userType,
  userId: _userId,
  pets: _pets,
  addToStoryMedia,
  onStoryAdded,
}: StoriesProps) {
  const { t } = useTranslation();
  const { addStory, addStoryFromFile, removeStory, getActiveStories } =
    useStories();
  const [viewingStoryRing, setViewingStoryRing] = useState<StoryRing | null>(
    null
  );
  const [viewingStoryIndex, setViewingStoryIndex] = useState(0);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [createStoryFile, setCreateStoryFile] = useState<File | null>(null);
  const [createStoryPreview, setCreateStoryPreview] = useState<string | null>(
    null
  );
  const [createStoryIsVideo, setCreateStoryIsVideo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showMediaEditor, setShowMediaEditor] = useState(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const activeStories = getActiveStories();
  const latestStoryUrl =
    activeStories.length > 0
      ? activeStories[activeStories.length - 1]?.url
      : null;

  // When parent asks to add a story (e.g. from Create Post "Also share to Your Story"), apply it
  useEffect(() => {
    if (!addToStoryMedia?.url) return;
    addStory(addToStoryMedia.url, addToStoryMedia.isVideo);
    onStoryAdded?.();
  }, [addToStoryMedia?.url, addToStoryMedia?.isVideo, onStoryAdded, addStory]);

  const yourStoryEntry: StoryRing = {
    id: 'own',
    petName: t('social.yourStory'),
    petAvatar: latestStoryUrl ?? '➕',
    hasUnviewed: false,
    isOwn: true,
    items: activeStories,
  };

  const addStoryEntry: StoryRing = {
    id: 'add',
    petName: t('social.addStory'),
    petAvatar: '➕',
    hasUnviewed: false,
    isOwn: true,
    items: [],
  };

  /** Always show "Your Story" and "Add Story" so users can always add a new story. */
  const storyRings: StoryRing[] = [yourStoryEntry, addStoryEntry];

  const handleYourStoryClick = () => {
    if (activeStories.length > 0) {
      setViewingStoryRing(yourStoryEntry);
      setViewingStoryIndex(0);
    } else {
      setShowCreateStory(true);
    }
  };

  const handleStoryRingClick = (story: StoryRing) => {
    if (story.id === 'add') {
      setShowCreateStory(true);
    } else if (story.isOwn) {
      handleYourStoryClick();
    } else {
      setViewingStoryRing(story);
      setViewingStoryIndex(0);
    }
  };

  const handleCreateStoryFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isImage && !isVideo) {
      toast.error(t('social.addStoryModal.choosePhotoOrVideoToast'));
      return;
    }
    setCreateStoryPreview((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return URL.createObjectURL(file);
    });
    setCreateStoryFile(file);
    setCreateStoryIsVideo(isVideo);
  };

  const handleShareStory = async () => {
    if (!createStoryFile) {
      toast.error(t('social.addStoryModal.choosePhotoOrVideoToast'));
      return;
    }
    try {
      await addStoryFromFile(createStoryFile);
      setCreateStoryFile(null);
      if (createStoryPreview) URL.revokeObjectURL(createStoryPreview);
      setCreateStoryPreview(null);
      setShowCreateStory(false);
      toast.success(t('social.addStoryModal.storyAddedToast'));
    } catch {
      toast.error(t('social.addStoryModal.failedToAddStoryToast'));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCloseCreateStory = () => {
    setShowCreateStory(false);
    setCreateStoryFile(null);
    if (createStoryPreview) URL.revokeObjectURL(createStoryPreview);
    setCreateStoryPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const currentViewingItems = viewingStoryRing?.items ?? [];
  const currentItem = currentViewingItems[viewingStoryIndex];
  const goToNextStory = useCallback(() => {
    if (viewingStoryIndex < currentViewingItems.length - 1) {
      setViewingStoryIndex((i) => i + 1);
    } else {
      setViewingStoryRing(null);
    }
  }, [viewingStoryIndex, currentViewingItems.length]);
  const goToPrevStory = useCallback(() => {
    if (viewingStoryIndex > 0) {
      setViewingStoryIndex((i) => i - 1);
    }
  }, [viewingStoryIndex]);

  const handleDeleteCurrentStory = () => {
    if (currentItem) {
      removeStory(currentItem.id);
      if (currentViewingItems.length <= 1) {
        setViewingStoryRing(null);
      } else if (viewingStoryIndex >= currentViewingItems.length - 1) {
        setViewingStoryIndex((i) => Math.max(0, i - 1));
      }
      toast.success(t('social.storyRemoved'));
    }
  };

  // Auto-advance story after STORY_DURATION_MS
  useEffect(() => {
    if (!viewingStoryRing || !currentItem) return;
    progressIntervalRef.current = setInterval(() => {
      goToNextStory();
    }, STORY_DURATION_MS);
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [viewingStoryRing, viewingStoryIndex, currentItem?.id, goToNextStory]);

  if (viewingStoryRing && currentItem) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        {/* Story Viewer */}
        <div className="relative w-full max-w-md h-full bg-black">
          {/* Close and Delete (own story only) – z-20 so they sit above progress/header and stay clickable */}
          <div className="absolute top-4 right-4 z-20 flex items-center gap-2 pointer-events-auto">
            {viewingStoryRing.isOwn && (
              <button
                type="button"
                onClick={handleDeleteCurrentStory}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/80 hover:bg-red-500 text-white text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
                title={t('social.deleteStory')}
              >
                <Trash2 className="w-5 h-5" />
                {t('social.delete')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewingStoryRing(null)}
              className="p-2 rounded-lg text-white hover:bg-white/10"
              aria-label={t('common.close')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bars – one per story (Instagram style) */}
          <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-20 pointer-events-none">
            {currentViewingItems.map((item, i) => (
              <div
                key={item.id}
                className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
              >
                <div
                  className="h-full bg-white rounded-full transition-all duration-100"
                  style={{
                    width:
                      i < viewingStoryIndex
                        ? '100%'
                        : i === viewingStoryIndex
                          ? '100%'
                          : '0%',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Tap zones for prev/next */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1/3 z-10 cursor-pointer"
            onClick={goToPrevStory}
            aria-label={t('social.previousStory')}
          />
          <div
            className="absolute right-0 top-0 bottom-0 w-1/3 z-10 cursor-pointer"
            onClick={goToNextStory}
            aria-label={t('social.nextStory')}
          />

          {/* Visible left/right arrows for story navigation */}
          {viewingStoryIndex > 0 && (
            <button
              type="button"
              onClick={goToPrevStory}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors pointer-events-auto"
              aria-label={t('social.previousStory')}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {viewingStoryIndex < currentViewingItems.length - 1 && (
            <button
              type="button"
              onClick={goToNextStory}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors pointer-events-auto"
              aria-label={t('social.nextStory')}
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Story Header */}
          <div className="absolute top-8 left-0 right-0 flex items-center gap-3 p-4 z-20 pointer-events-none">
            <div className="flex items-center gap-3 pointer-events-auto">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center text-2xl border-2 border-white overflow-hidden">
                <StoryThumbnail
                  src={viewingStoryRing.petAvatar}
                  isVideo={currentItem.isVideo}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 text-left">
                <div className="text-white font-semibold">
                  {viewingStoryRing.petName}
                </div>
                <div className="text-white/80 text-sm">
                  {currentViewingItems.length > 1
                    ? `${viewingStoryIndex + 1} / ${currentViewingItems.length}`
                    : t('social.yourStory')}
                </div>
              </div>
            </div>
          </div>

          {/* Story Content */}
          <StoryMediaView
            imageUrl={currentItem.url}
            isVideo={currentItem.isVideo}
          />

          {/* Story Actions (Bottom) */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder={t('social.replyToStory')}
                className="flex-1 px-4 py-2 bg-white/20 border border-white/30 rounded-full text-white placeholder-white/60 outline-none"
              />
              <button className="text-white">
                <span className="text-2xl">❤️</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-[#16151D] rounded-2xl p-4 border border-[#E2DFEA] dark:border-white/8">
        <div className="flex justify-center gap-6 overflow-x-auto py-1">
          {storyRings.map((story) => (
            <button
              key={story.id}
              onClick={() => handleStoryRingClick(story)}
              className="flex flex-col items-center gap-2 flex-shrink-0"
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl overflow-hidden ${
                  story.id === 'add'
                    ? 'border-2 border-dashed border-[#936FEF] dark:border-purple-500/60 bg-[#F0EFF5] dark:bg-[#0F0E16]'
                    : story.isOwn
                      ? 'border-2 border-[#E2DFEA] dark:border-white/20 bg-[#F0EFF5] dark:bg-[#0F0E16]'
                      : story.hasUnviewed
                        ? 'p-0.5'
                        : 'border-2 border-[#E2DFEA] dark:border-white/20'
                }`}
                style={
                  !story.isOwn && story.hasUnviewed
                    ? {
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                      }
                    : undefined
                }
              >
                <div
                  className={`w-full h-full rounded-full flex items-center justify-center overflow-hidden relative ${
                    !story.isOwn && 'bg-white dark:bg-[#16151D]'
                  }`}
                >
                  <StoryThumbnail
                    src={story.petAvatar}
                    isVideo={story.isVideo}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <span
                className="text-xs text-center max-w-[64px] truncate text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {story.petName}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Create Story modal */}
      {showCreateStory && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-2xl border dark:border-white/8 max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#E2DFEA] dark:border-white/8">
              <h2
                className="text-lg text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('social.addStoryModal.title')}
              </h2>
              <button
                onClick={handleCloseCreateStory}
                className="hover:bg-[#F0EFF5] dark:hover:bg-white/5 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={handleCreateStoryFile}
              />
              {createStoryPreview ? (
                <div className="space-y-4">
                  <div className="relative aspect-[9/16] max-h-80 rounded-xl overflow-hidden bg-[#F0EFF5] dark:bg-[#0F0E16]">
                    {createStoryIsVideo ? (
                      <video
                        src={createStoryPreview}
                        className="w-full h-full object-contain"
                        controls
                        playsInline
                        muted
                      />
                    ) : (
                      <>
                        <img
                          src={createStoryPreview}
                          alt={t('social.preview')}
                          className="w-full h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMediaEditor(true);
                          }}
                          className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-black/60 hover:bg-black/80 text-white text-sm font-medium"
                        >
                          <Pencil className="w-4 h-4" />
                          {t('social.addStoryModal.cropAndEdit')}
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 px-4 py-2 rounded-xl border-2 border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white font-medium"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {t('social.addStoryModal.changePhotoOrVideo')}
                    </button>
                    <button
                      type="button"
                      onClick={handleShareStory}
                      className="flex-1 px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      style={{
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      <Plus className="w-5 h-5" />
                      {t('social.addStoryModal.shareToStory')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-[#E2DFEA] dark:border-white/20 rounded-2xl p-12 text-center hover:border-[#936FEF] dark:hover:border-[#936FEF] hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                >
                  <div className="flex justify-center gap-4 mb-3">
                    <ImageIcon className="w-12 h-12 text-[#464452] dark:text-white/40" />
                    <Video className="w-12 h-12 text-[#464452] dark:text-white/40" />
                  </div>
                  <p
                    className="text-[#464452] dark:text-white/70 mb-1"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.addStoryModal.choosePhotoOrVideo')}
                  </p>
                  <p
                    className="text-sm text-[#464452] dark:text-white/50"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('social.addStoryModal.fileTypes')}
                  </p>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Media Editor for story photo */}
      {showMediaEditor && createStoryPreview && !createStoryIsVideo && (
        <MediaEditorModal
          imageUrl={createStoryPreview}
          isVideo={false}
          onClose={() => setShowMediaEditor(false)}
          onDone={(file) => {
            setCreateStoryFile(file);
            if (createStoryPreview) URL.revokeObjectURL(createStoryPreview);
            setCreateStoryPreview(URL.createObjectURL(file));
            setShowMediaEditor(false);
          }}
        />
      )}
    </>
  );
}
